import { hasMinRole, type SystemRole } from "@/lib/constants";

/**
 * Sentralt feature flag-register (VIB-1396, første steg før juli-bølgene).
 *
 * Katalog-mønsteret à la `EVENT_TYPES` (`src/lib/event-registry.ts`) og
 * `NOTIFICATION_TYPES` (`src/lib/notification-catalog.ts`): `Record<Key, Meta>`
 * tvinger tsc til å kreve en oppføring for hver nøkkel i unionen, så
 * registeret og bruken av det ikke kan drifte fra hverandre.
 *
 * Bakgrunn / kontrakt: «Lanseringsmodell»-seksjonen i
 * `docs/featureliste-2026-07/bolger/00-autonom-implementeringsplan.md`
 * (Erik 16.07). Bølgene merges til `main` mørke bak disse flaggene — Erik
 * ruller ut per bølge ved å flytte rollout-nivået, uavhengig av deploy.
 */

/**
 * Rollout-nivå — IKKE boolean. Reflekterer hvilken systemrolle som ser
 * funksjonen som PÅ. Nivåene er kumulative via samme rollehierarki som
 * `hasMinRole` (external < user < admin < superadmin):
 * - "off": ingen ser den.
 * - "superadmin": kun superadmin (Erik/Vibbing) — for tidlig, uverifisert kode.
 * - "admin": admin + superadmin — Sølvrev-ledelsen tester før alle ansatte.
 * - "all": alle innloggede brukere.
 */
export type FeatureFlagRolloutLevel = "off" | "superadmin" | "admin" | "all";

export const FEATURE_FLAG_ROLLOUT_LEVELS: {
  value: FeatureFlagRolloutLevel;
  label: string;
}[] = [
  { value: "off", label: "Av" },
  { value: "superadmin", label: "Kun superadmin" },
  { value: "admin", label: "Admin og superadmin" },
  { value: "all", label: "Alle" },
];

export interface FeatureFlagMeta {
  /** Kort visningsnavn i Funksjonsbrytere-panelet. */
  label: string;
  /** Én-to setninger: hva flagget styrer. */
  description: string;
  /** Hvem som eier flagget (person/bølge) — flaggdisiplin krever alltid en eier. */
  owner: string;
  /**
   * Linear-referanse for opprydning (fjerning av flagget + if-grenene) når
   * nivået har stått på "all" en periode. ALDRI et evigvarende flagg uten
   * en plan for å fjerne det.
   */
  cleanupRef: string;
  /**
   * "active" (default semantikk): flagget SKAL konsumeres av minst én
   *   `useFeatureFlag(key)`-referanse i `src/` — CI-testen under håndhever
   *   dette.
   * "reserved": registrert i påvente av en kommende bølge som ennå ikke har
   *   bygget koden som leser flagget. Unntatt fra "i bruk"-kravet, men `cleanupRef`
   *   MÅ da peke til bølgen/issuen som skal konsumere det.
   */
  status: "active" | "reserved";
  /**
   * Roller som ser flagget som PÅ så lenge nivået ikke er "off", uavhengig av
   * rollehierarkiet. For roller som finnes bare fordi funksjonen finnes (CRM-4:
   * motebooker), slik at de kan testes sammen med superadmin før "all".
   */
  includeRoles?: readonly string[];
}

/**
 * Legg til nye nøkler her når en bølge trenger et flagg. Konvensjon:
 * `feature_<navn>` (snake_case, matcher `system_config.key`).
 */
export type FeatureFlagKey =
  | "feature_offer_pipeline"
  | "feature_pm_inbox_approval"
  | "feature_voucher_image_capture"
  | "feature_budget_revision"
  | "feature_tm_upsell"
  | "feature_contracts"
  | "feature_kpi_reconciliation"
  | "feature_offer_workspace"
  | "feature_freelancer_projects"
  | "feature_freelancer_bank"
  | "feature_dokumentarkiv"
  | "feature_extras_payment_flow"
  | "feature_crm_kundekort"
  | "feature_crm_ko_pipe"
  | "feature_crm_signaler"
  | "feature_crm_leads_moter"
  | "feature_equipment"
  | "feature_locations"
  | "feature_maaltall_v2"
  | "feature_maaltall_agent"
  | "feature_chat_rom";

export const FEATURE_FLAGS: Record<FeatureFlagKey, FeatureFlagMeta> = {
  feature_chat_rom: {
    label: "Chat-rom",
    description:
      "Siden «Chat» med kanaler for prosjekter (samme strøm som Prosjektoppdateringer), hele huset og direktemeldinger, med reaksjoner, @-nevning, systemmeldinger og AI-oppsummering av uleste (VIB-3022). Erstatter den flytende chatten for rollene flagget er på for. Av: menyvalget er skjult og den flytende chatten vises som før.",
    owner: "Erik (VIB-3022)",
    cleanupRef:
      "VIB-3022: fjern flagget, ChatLauncher og de gamle komponentene i src/components/chat når nivået har stått på \"all\" i to uker.",
    status: "active",
  },
  feature_maaltall_agent: {
    label: "Spør om tallene",
    description:
      "Tall-agent på Måltall for ledelsen (VIB-2992): svarer på spørsmål om tallene med de samme beregningene som widgetene, og foreslår tiltak bare fra de faste sjekkene. Ansatte pseudonymiseres før noe sendes til Anthropic. Krever ny Måltall. Bare admin og superadmin, uansett nivå.",
    owner: "Erik (Måltall)",
    cleanupRef:
      "VIB-2992: fjern flagget og if-grenen i WidgetDashboard når nivået har stått på \"admin\" i to uker og loggen i maaltall_agent_log er gjennomgått.",
    status: "active",
  },
  feature_maaltall_v2: {
    label: "Ny Måltall",
    description:
      "Ryddet Måltall etter godkjent wireframe (VIB-2990): KPI-rad, kompakt bonusmål, «Dette bør du se på», faktureringsgrad med kapasitetsfordeling, prognose, prosjekter som venter og egne visninger for ansatte. Av: dagens Måltall.",
    owner: "Erik (Måltall)",
    cleanupRef:
      "VIB-2990: fjern flagget, gamle widgets og if-grenene når nivået har stått på \"all\" i to uker, og kjør widget_configs-DML (VIB-2993).",
    status: "active",
  },
  feature_locations: {
    label: "Lokasjoner",
    description:
      "Lokasjonsbanken. Gir /lokasjoner med kort og tabell, lokasjonskortet med bilder, ny lokasjon med stedssøk (Google) og posisjon fra Kartverket, og gruppen «Lokasjoner» i søket. Kart, prosjektkobling og deling kommer bak samme flagg. Av: ingen av flatene vises. Menynøkkelen «lokasjoner» må i tillegg være på for rollen.",
    owner: "Erik (lokasjonsbanken)",
    cleanupRef:
      "VIB-2942 (lansering): fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_equipment: {
    label: "Utstyr",
    description:
      "Utstyrsdatabasen. Gir /utstyr med registeret (Oslo og Bergen), utstyrskortet, registrering og forsikringsoversikten, gruppen «Utstyr» i søket og feltene Avdeling og Utstyrsansvarlig under Brukere. Booking, pakkeliste og service kommer bak samme flagg. Av: ingen av flatene vises. Menynøkkelen «utstyr» må i tillegg være på for rollen.",
    owner: "Erik (utstyrsdatabasen)",
    cleanupRef:
      "VIB-2922 (lansering): fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_crm_leads_moter: {
    label: "Leads, booking, referat",
    description:
      "CRM fase 4. Leads-fanen for møtebookere (ett lead om gangen fra en usynlig pool), lister og segmenter for DL, bookingdialogen med Google Kalender og Meet, påminnelser på e-post og SMS, transkripsjon og møtereferat, ringeaktivitet og streak. Cron-jobbene calendar-sync-meetings og meet-fetch-transcripts leser flagget selv. Rollen Møtebooker ser flagget som på så lenge nivået ikke er Av. Av: ingen leads, møter eller påminnelser.",
    owner: "Erik (CRM fase 4)",
    cleanupRef:
      "CRM-4.9 — Linear-issue for fjerning opprettes av Erik; fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
    includeRoles: ["motebooker"],
  },
  feature_crm_ko_pipe: {
    label: "Salgsmuligheter: kø og pipe",
    description:
      "CRM fase 2. Gir /salgsmuligheter med Oppdag (køen med oppfølginger) og Pipe (Outreach, Møte, Tilbud), prosesser og oppfølginger på kundekortet, «Lag tilbud» fra en prosess, mailutkast og fem varsler. Regel-cronen for oppfølginger og varselet «Dagens oppfølginger» leser flagget selv. Av: ingen oppfølginger lages og ingen varsler sendes.",
    owner: "Erik (CRM fase 2)",
    cleanupRef:
      "CRM-2.9 — Linear-issue for fjerning opprettes av Erik; fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_crm_signaler: {
    label: "Salgsmuligheter: signaler og overvåkning",
    description:
      "CRM fase 3. Bedriftsinfo fra Brønnøysund og overvåkning per kilde på kundekortet, signalagenter (standardkatalog og egne) med Agenter-fanen, signalkort i køen og kostnadspanelet under Drift. Cron-jobbene brreg-sync-customers, run-signal-agents og crm_expire_signals leser flagget selv. Av: ingen signaler lages og ingenting hentes fra Brønnøysund, Apify eller Claude.",
    owner: "Erik (CRM fase 3)",
    cleanupRef:
      "CRM-3.6 — Linear-issue for fjerning opprettes av Erik; fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_crm_kundekort: {
    label: "Kundekort v2: kontaktpersoner, kontaktlogg, status, Gmail",
    description:
      "CRM fase 1. Kundekortet får hero med kundestatus (varm, medium, kald) og eiere, kontaktpersoner, kontaktlogg og «Send e-post» som selgeren via Gmail. Edge-funksjonene gmail-send, gmail-sync og crm-dispatch-outbox leser flagget selv. Av: kundekortet er som før, og ingen e-post sendes eller synkes.",
    owner: "Erik (CRM fase 1)",
    cleanupRef:
      "CRM-1.7 — Linear-issue for fjerning opprettes av Erik; fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_dokumentarkiv: {
    label: "Dokumentarkiv",
    description:
      "Gir /hjelp en egen Dokumenter-seksjon med mapper (HMS, Rutiner, Styreprotokoller, Personalhåndbok + egne), nivåtilganger per mappe, opplastede filer som vises inline i siden, og dokumenter som skrives i systemet med en read-only visning for dem uten skrivetilgang. Av: /hjelp er nøyaktig som før, uten Dokumenter-punktet.",
    owner: "Erik",
    cleanupRef:
      "VIB-2703 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_offer_pipeline: {
    label: "Vektet salgspipeline",
    description:
      "Sannsynlighetskategori + 45 dagers gyldighet på tilbud, kanban-board på Tilbud-siden, KPI-kortet «Vektet tilbudspipeline» på salgsdashboardet, utløpsvarsling og påkrevd tapsbegrunnelse. Én pakke, ett flagg (Erik 18.07).",
    owner: "Erik (bølge 6)",
    cleanupRef: "VIB-1304 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_pm_inbox_approval: {
    label: "PM-innboks: godkjenn bilag i modal",
    description:
      "Bytter «Godkjenn»-knappen i PM-innboksens bilagsrad og i BilagTab fra deep-link/inline-panel til en selvforsynt godkjenningsdialog (modal) med inline budsjettpanel (fase, beløp, ført bilagskost, differanse). Av: dagens deep-link + inline-ekspansjon (uendret).",
    owner: "Erik (bølge 3)",
    cleanupRef: "VIB-1226 — fjern flagget og if-grenene (gamle deep-link/inline-panel-sti) når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_voucher_image_capture: {
    label: "Bilag: fang side-bilde i godkjenningskø",
    description:
      "Fanger bilagets side-bilde fra PowerOffice sin /VoucherApproval-kø (rute 1) mens bilaget er aktivt der, og lagrer det i en privat Storage-bucket. Dekker vinduet der bilaget er godkjent (forlatt PO-køen) men ennå ikke bokført — rute 1 er da stengt og rute 2 (bokført dokument) har ikke bildet ennå. Kopien slettes automatisk når bilaget bokføres.",
    owner: "Erik (bølge 3)",
    cleanupRef: "VIB-1046 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    // VIB-1046: konsumeres KUN av edge functions (sync-poweroffice-voucher-
    // approvals, get-voucher-document, sync-poweroffice-vouchers) via det
    // dokumenterte edge-function-lesemønsteret nedenfor — ingen
    // useFeatureFlag()-kall i src/ (funksjonen har ingen egen UI, den endrer
    // kun hvilke bytes get-voucher-document returnerer). "reserved" er derfor
    // permanent riktig status her, ikke et tegn på ubygd kode.
    status: "reserved",
  },
  feature_budget_revision: {
    label: "Revider budsjett",
    description:
      "Lar PL/admin revidere et godkjent hovedbudsjett på Tilbud-fanen (endre beløp/timer/fordeling, legge til/fjerne linjer) med begrunnelse, vern mot reduksjon under loggede/fullførte timer og låst måned, og et samlende revisjonsspor i prosjektloggen.",
    owner: "Erik (bølge 4)",
    cleanupRef: "VIB-1202 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_tm_upsell: {
    label: "Mersalg på medgått tid (T&M)",
    description:
      "Lar PL opprette mersalg med faktureringsform «Medgått tid + materiell» (T&M) i tillegg til fastpris (UpsellEditDialog). T&M-mersalgets loggede timer teller 1:1 i faktureringsgrad (get_billing_rates-RPC-en, KPI-slice VIB-994). T&M-UI-slicen legger til modusvalget, MersalgTab-badge/«fakturert hittil», og en T&M-fakturaseksjon på Faktura-siden scoped til hvert godkjente T&M-mersalg.",
    owner: "Erik (bølge 4)",
    cleanupRef: "VIB-994 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_contracts: {
    label: "Kontrakter (statister/eksterne/location)",
    description:
      "Kontrakt-knappene i Statister-tabben: generer/forhåndsvis kontrakt-PDF (VIB-1281), send til signering (VIB-1282). Rolle-feltet (VIB-1387) er IKKE bak dette flagget — det shippes uten flagg per flaggplanen.",
    owner: "Erik (bølge 8)",
    cleanupRef: "VIB-676 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode og alle tre sub-issues (1281/1282/1283) er Done.",
    status: "active",
  },
  feature_extras_payment_flow: {
    label: "Utbetalingsflyt (statister)",
    description:
      "«Klar for utbetaling»-knappen på prosjektets statistliste, og utbetalingslisten DL henter under Rapporter. PL melder klar, daglig leder får varsel i bjella og markerer utbetalt. Listen er dobbeltgatet på admin/superadmin i tillegg til flagget.",
    owner: "Erik (V1, William-lista)",
    cleanupRef:
      "VIB-2092 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode og rutinen er innarbeidet. Samtidig kan legacy-kolonnen payment_status droppes (contract-steget i expand/contract).",
    status: "active",
  },
  feature_kpi_reconciliation: {
    label: "Avstemte KPI-perioder",
    description:
      "Viser avstemte økonomitall og faktureringsgrad basert på den konfigurerte avstemmingsdagen, med tydelig periode og neste avstemmingsdato.",
    owner: "Erik (Terje-sporet)",
    cleanupRef: "VIB-1622 — fjern flagget og legacy-perioden når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_offer_workspace: {
    label: "Tilbudsarbeidsrom",
    description:
      "Tilbudet får grenene Grunnlag, Idé, Budsjett, Presentasjon og Tilbud (VIB-2978). Grunnlag har målgruppe, budsjettramme og kilder fra kundekortet som AI skal bruke. Idé, Presentasjon og Tilbud viser «Kommer» til fasene deres er levert. Av: dagens tilbudsside uten grener.",
    owner: "Erik (tilbudsarbeidsrom)",
    cleanupRef:
      "VIB-2978 — fjern flagget og if-grenene når nivået har stått på \"all\" i fire uker (docs/tilbudsarbeidsrom/00-kjoreplan.md §12).",
    status: "active",
  },
  feature_freelancer_projects: {
    label: "Frilans på prosjekt",
    description:
      "Frilanser på eksterne kostnadslinjer i Timetildeling, «Koble til frilanser» i bilagstabellen, frilansere i Team & eierskap, prosjekter og «Sist brukt» i frilanserbanken, og siden Leverandører. Krever også «Frilanserbanken». Skriver aldri kost. Av: frilanserbanken uten prosjektkobling.",
    owner: "Erik (VIB-2927)",
    cleanupRef: "VIB-2927 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
  feature_freelancer_bank: {
    label: "Frilanserbanken",
    description:
      "Siden «Frilansere» med fagfelt, vurderinger, priser, kontaktinfo og kommentarlogg. Menyen vises bare når både flagget og menynøkkelen «frilansere» er på for rollen. Av: menyvalget er skjult og sidene viser «Ikke tilgjengelig ennå».",
    owner: "Erik (VIB-2925)",
    cleanupRef: "VIB-2925 — fjern flagget og if-grenene når nivået har stått på \"all\" en periode.",
    status: "active",
  },
};

/**
 * `system_config.key`-verdien for et flagg er identisk med registernøkkelen
 * (ingen ekstra prefiksing) — eksportert som funksjon for å holde kallsteder
 * eksplisitte om at det er en bevisst 1:1-mapping, ikke en tilfeldighet.
 */
export function systemConfigKeyForFlag(key: FeatureFlagKey): string {
  return key;
}

/**
 * Dedikert event_type for feature flag-nivåendringer (audit). Feature flags
 * er systemomfattende (ikke prosjekt-scopet), så de kan IKKE logges via
 * `project_events` — den tabellen har `project_id NOT NULL` og mates
 * utelukkende av `EVENT_TYPES`-registeret i `src/lib/event-registry.ts`
 * (kun brukt av ProjectLogTab). Systemomfattende config-endringer bruker i
 * stedet det etablerte `event_log`-mønsteret (jf. `system_config.updated` i
 * `src/hooks/use-system-config.ts`), logget via `logEvent()`. Denne
 * konstanten er eksportert nettopp for å unngå inline-literal på kallstedet.
 */
export const FEATURE_FLAG_EVENT_TYPE = "feature_flag_level_changed" as const;

/**
 * Edge function-lesemønster: en edge function som trenger å vite om et
 * flagg er på, leser samme `system_config`-rad direkte:
 *
 *   const { data } = await supabase
 *     .from("system_config")
 *     .select("value")
 *     .eq("key", "feature_<navn>")
 *     .maybeSingle();
 *   const level = (data?.value as FeatureFlagRolloutLevel | undefined) ?? "off";
 *
 * Manglende rad (data er null) MÅ tolkes som `"off"` — ALDRI anta at
 * fraværende konfig betyr på. Seed-migrasjonen garanterer at raden finnes
 * fra og med at flagget registreres her, men edge functions kjørt mot en
 * branch/miljø der migrasjonen ikke har rukket å kjøre skal likevel feile
 * trygt til av.
 */
export function resolveFeatureFlagLevel(
  level: FeatureFlagRolloutLevel | null | undefined,
  role: string | null | undefined,
  key?: FeatureFlagKey,
): boolean {
  if (!level || level === "off") return false;
  if (level === "all") return true;
  if (key && role && FEATURE_FLAGS[key].includeRoles?.includes(role)) return true;
  // level er her innsnevret til "superadmin" | "admin", som begge er gyldige
  // SystemRole-verdier — hasMinRole gir kumulativ semantikk (admin ser også
  // det som er satt til "admin", superadmin ser alt).
  return hasMinRole(role ?? "user", level as SystemRole);
}
